import {THEME} from '@wandb/weave/common/css/color.styles';
import {Select} from '@wandb/weave/components/Form/Select';
import {Icon, type IconName} from '@wandb/weave/components/Icon';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React from 'react';

import {type SecretPool} from './SecretPoolPill';

type ScopeOption = {
  value: SecretPool;
  label: string;
  subtext: string;
  icon: IconName;
  isDisabled?: boolean;
};

const TEAM_ADMIN_ONLY_TOOLTIP = 'Only team admins can add team keys';

const renderScopeOption = (option: ScopeOption, context: 'menu' | 'value') => {
  const content = (
    <div className="flex items-center gap-8">
      <Icon name={option.icon} width={18} height={18} />
      <div className="flex flex-col">
        <span className="text-[14px] font-semibold">{option.label}</span>
        {/* The closed control is a fixed 32px row, so a second line there
            spills out of the box and over the banner below it; the subtext
            belongs to the menu rows, which size to their content. */}
        {context === 'menu' && (
          <span className="text-[12px]" style={{color: THEME.textTertiary}}>
            {option.subtext}
          </span>
        )}
      </div>
    </div>
  );
  if (option.isDisabled && context === 'menu') {
    return <Tooltip trigger={content} content={TEAM_ADMIN_ONLY_TOOLTIP} />;
  }
  return content;
};

/** "Save as" personal/team scope selector; the team option is admin-gated. */
export const SecretScopeSelect: React.FC<{
  scope: SecretPool;
  onScopeChange: (scope: SecretPool) => void;
  isTeamAdmin: boolean;
}> = ({scope, onScopeChange, isTeamAdmin}) => {
  const scopeOptions: ScopeOption[] = [
    {
      value: 'personal',
      label: 'Personal',
      subtext: 'Only you. Default',
      icon: 'user-profile-personal',
    },
    {
      value: 'team',
      label: 'Team',
      subtext: 'Shared with team',
      icon: 'users-team',
      isDisabled: !isTeamAdmin,
    },
  ];
  return (
    <div className="flex flex-col gap-8">
      <div className="text-[16px] font-semibold">Save as</div>
      <Select<ScopeOption, false>
        value={scopeOptions.find(opt => opt.value === scope)}
        onChange={option => {
          if (option) {
            onScopeChange(option.value);
          }
        }}
        options={scopeOptions}
        isOptionDisabled={option => Boolean(option.isDisabled)}
        formatOptionLabel={(option, meta) =>
          renderScopeOption(option, meta.context)
        }
      />
    </div>
  );
};
